import { z } from "zod";
export const ItemPedidoEntradaSchema = z.object({
    productoId: z.string().uuid("ID de producto inválido"),
    cantidad: z.number().int().min(1, "La cantidad mínima es 1").default(1),
    notas: z.string().max(240, "Las notas no pueden superar 240 caracteres").optional().nullable(),
});
export const CrearPedidoSchema = z.object({
    mesaId: z.string().uuid("ID de mesa inválido"),
    notas: z.string().max(500, "Las notas generales no pueden superar 500 caracteres").optional().nullable(),
    items: z.array(ItemPedidoEntradaSchema).min(1, "El pedido debe contener al menos un producto"),
    // Ver Factura.idempotencyKey / caja.schema.ts: mismo mecanismo para que un
    // reintento offline de "crear pedido" no duplique la comanda.
    idempotencyKey: z.string().max(64).optional().nullable(),
});
export const CrearPedidoDomicilioPublicoSchema = z.object({
    tipo: z.enum(["SALON", "DOMICILIO", "PARA_LLEVAR"]).default("DOMICILIO"),
    clienteNombre: z.string().min(2, "El nombre del cliente debe tener al menos 2 caracteres").max(120),
    clienteTelefono: z.string().max(30).optional().nullable(),
    direccionEntrega: z.string().max(240).optional().nullable(),
    barrioRef: z.string().max(120).optional().nullable(),
    medioPagoEstimado: z.string().max(60).optional().nullable(),
    notas: z.string().max(500).optional().nullable(),
    items: z.array(ItemPedidoEntradaSchema).min(1, "El pedido debe contener al menos un producto"),
});
export const ActualizarEstadoDomicilioSchema = z
    .object({
    estadoDomicilio: z.enum(["NUEVO", "ACEPTADO", "EN_PREPARACION", "DESPACHADO", "ENTREGADO", "CANCELADO"]),
    repartidorNombre: z.string().max(100).optional().nullable(),
    motivoCancelacion: z.string().min(3, "Cuéntanos brevemente por qué se cancela").max(300).optional().nullable(),
})
    .refine((data) => data.estadoDomicilio !== "CANCELADO" || !!data.motivoCancelacion, {
    message: "El motivo de cancelación es obligatorio.",
    path: ["motivoCancelacion"],
});
// Cancelar un pedido/comanda YA ENVIADO a cocina (p.ej. la mesera se
// equivocó al tomar el pedido). El motivo es obligatorio para que quede
// trazabilidad de quién y por qué — se ve luego en el reporte de admin.
export const CancelarPedidoSchema = z.object({
    motivo: z.string().min(3, "Cuéntanos brevemente por qué se cancela").max(300),
});
// Corregir UN producto suelto de una comanda ya enviada (sin cancelar todo
// el pedido) — p.ej. la mesera se equivocó de cantidad. El motivo también
// es obligatorio, igual que al cancelar el pedido completo.
export const EliminarItemPedidoSchema = z.object({
    motivo: z.string().min(3, "Cuéntanos brevemente por qué se quita este producto").max(300),
});
export const EditarCantidadItemPedidoSchema = z.object({
    cantidad: z.number().int().min(1, "La cantidad mínima es 1"),
    motivo: z.string().min(3, "Cuéntanos brevemente por qué se corrige la cantidad").max(300),
});
// Nota de la mesera sobre un producto YA ENVIADO, para que el administrador
// la vea al cobrar (ej. "el cliente ya no quiere esto, bórralo"). No pide
// motivo largo como las correcciones de arriba -- es un aviso rápido, no
// una justificación. Cadena vacía = quitar la nota (la deja en null).
export const NotaMeseraItemSchema = z.object({
    nota: z.string().trim().max(240, "La nota es demasiado larga"),
});
// ------------------------------------------------------------------
// Respuestas (lo que el backend REALMENTE devuelve). Igual que en
// caja.schema.ts: z.coerce.number() en los montos, porque Prisma
// serializa los `Decimal` como string (ver PROGRESS.md sección 5b/7).
// ------------------------------------------------------------------
const MontoCoercible = z.coerce.number();
export const PedidoItemDetalleSchema = z.object({
    id: z.string(),
    pedidoId: z.string(),
    productoId: z.string(),
    impresoraId: z.string().nullable().optional(),
    cantidad: z.number(),
    precioUnitario: MontoCoercible,
    subtotal: MontoCoercible,
    notas: z.string().nullable().optional(),
    cantidadOriginal: z.number().nullable().optional(),
    motivoModificacion: z.string().nullable().optional(),
    notaMesera: z.string().nullable().optional(),
    notaMeseraEn: z.string().nullable().optional(),
    producto: z.object({
        id: z.string(),
        nombre: z.string(),
        precio: MontoCoercible,
        categoria: z.object({
            id: z.string(),
            nombre: z.string(),
            color: z.string().nullable().optional(),
        }),
    }),
    impresora: z.object({ id: z.string(), nombre: z.string(), ip: z.string() }).nullable().optional(),
});
export const PedidoDetalleSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    mesaId: z.string().nullable().optional(),
    mesaSesionId: z.string().nullable().optional(),
    usuarioId: z.string().nullable().optional(),
    estado: z.string(),
    total: MontoCoercible,
    notas: z.string().nullable().optional(),
    creadoEn: z.string(),
    items: z.array(PedidoItemDetalleSchema),
    usuario: z.object({ id: z.string(), nombre: z.string(), usuario: z.string(), rol: z.string() }).nullable().optional(),
});
export const DomicilioConItemsSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    tipo: z.enum(["SALON", "DOMICILIO", "PARA_LLEVAR"]),
    estado: z.string(),
    estadoDomicilio: z.enum(["NUEVO", "ACEPTADO", "EN_PREPARACION", "DESPACHADO", "ENTREGADO", "CANCELADO"]),
    clienteNombre: z.string(),
    clienteTelefono: z.string().nullable().optional(),
    direccionEntrega: z.string().nullable().optional(),
    barrioRef: z.string().nullable().optional(),
    medioPagoEstimado: z.string().nullable().optional(),
    repartidorNombre: z.string().nullable().optional(),
    motivoCancelacion: z.string().nullable().optional(),
    total: MontoCoercible,
    notas: z.string().nullable().optional(),
    creadoEn: z.string(),
    items: z.array(PedidoItemDetalleSchema),
});
//# sourceMappingURL=pedido.schema.js.map